import type { Api } from "../../../data/api";
import type { SignatureValue } from "./Unterschrift";
import { currentLang, t } from "../../../lib/sprache";

/**
 * Gespeicherte Unterschrift je Person im Team (PARITAET-12, 0080): Name und Bild (PNG oder JPEG als data-URL, höchstens
 * 200 KB) in der eigenen Zeile von user_preferences. Nur die Person selbst liest und schreibt sie (Policy prefs_own). Beim
 * Gegenzeichnen füllt sie das Unterschriftsfeld vor, wie die gespeicherte Unterschrift der Creator (talent_signatures).
 */

export const SIGNATURE_IMAGE_MAX = 200_000;

export interface SavedSignature { name: string | null; image: string | null }

/** Gültiges Bild für die Unterschrift (wie der Datenbank-Check) */
export function isSignatureImage(v: string | null | undefined): v is string {
  return !!v && /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/.test(v) && v.length <= SIGNATURE_IMAGE_MAX;
}

/** Vorbelegung des Unterschriftsfelds: gespeichertes Bild; der Name bleibt der des Kontos (Beweiskette) */
export function prefillSignature(current: SignatureValue, saved: SavedSignature | null | undefined): SignatureValue {
  if (!saved || !isSignatureImage(saved.image) || current.dataUrl) return current;
  return { ...current, dataUrl: saved.image };
}

export async function loadSavedSignature(api: Api, userId: string): Promise<SavedSignature | null> {
  const rows = await api.select<{ signature_name: string | null; signature_image: string | null }>("user_preferences", {
    columns: "signature_name, signature_image", filters: [["user_id", "eq", userId]],
  }).catch(() => []);
  const r = rows[0];
  return r && (r.signature_image || r.signature_name) ? { name: r.signature_name, image: r.signature_image } : null;
}

/** Speichern bzw. mit null entfernen (eigene Zeile anlegen, falls es noch keine gibt) */
export async function saveSignatureFor(api: Api, userId: string, value: SignatureValue | null): Promise<true> {
  if (value && !isSignatureImage(value.dataUrl)) throw new Error(t("Bitte zuerst eine Unterschrift zeichnen oder ein Bild wählen.", "Please draw a signature or choose an image first."));
  if (value && value.name.trim().length < 2) throw new Error(t("Bitte deinen vollständigen Namen angeben.", "Please enter your full name."));
  const patch = { signature_name: value ? value.name.trim().slice(0, 120) : null, signature_image: value ? value.dataUrl : null, updated_at: new Date().toISOString() };
  const rows = await api.update<{ user_id: string }>("user_preferences", [["user_id", "eq", userId]], patch, { columns: "user_id" });
  if (!rows.length) await api.insert("user_preferences", { user_id: userId, ...patch }, { columns: "user_id" });
  return true;
}

/**
 * Eingescannte Unterschrift (PNG oder JPG bis 5 MB, wie im Altportal) als PNG-data-URL, verkleinert auf höchstens 600 Pixel
 * Breite und 200 KB; im Browser.
 */
export async function signatureImageFromFile(file: Blob, lang?: "de" | "en"): Promise<string> {
  // Sprache des Unterzeichners (öffentliche Signierseite, PARITAET-17), sonst die der Oberfläche
  const t = (de: string, en: string) => ((lang ?? currentLang()) === "en" ? en : de);
  if (!["image/png", "image/jpeg"].includes(file.type)) throw new Error(t("Bitte ein PNG- oder JPG-Bild wählen.", "Please choose a PNG or JPG image."));
  if (file.size <= 0 || file.size > 5 * 1024 * 1024) throw new Error(t("Das Bild ist leer oder größer als 5 MB.", "The image is empty or larger than 5 MB."));
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error(t("Das Bild ließ sich nicht lesen.", "The image could not be read.")));
      i.src = url;
    });
    for (const width of [600, 480, 360, 240]) {
      const scale = Math.min(1, width / img.naturalWidth, (width * 0.3) / img.naturalHeight);
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(img.naturalWidth * scale));
      c.height = Math.max(1, Math.round(img.naturalHeight * scale));
      c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
      const data = c.toDataURL("image/png");
      if (isSignatureImage(data)) return data;
    }
    throw new Error(t("Die Unterschrift ist zu groß. Bitte ein kleineres Bild wählen.", "The signature is too large. Please choose a smaller image."));
  } finally {
    URL.revokeObjectURL(url);
  }
}
